import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import PageShell from '../ui/PageShell';
import { PageHeader } from '../ui/PageHeader';
import PageBackLink from '../ui/PageBackLink';
import Button from '../ui/Button';
import Footer from '../Footer';
import { CookiePreferencesModal } from './CookiePreferencesModal';
import apiService from '../../services/ApiService';
import { useAuth } from '../../contexts/AuthContext';

const PrivacyCenterPage: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const [cookieOpen, setCookieOpen] = useState(false);
  const [status, setStatus] = useState('');
  const [exportBusy, setExportBusy] = useState(false);

  return (
    <PageShell>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <PageBackLink label="Back to Garage" to="/garage" className="mb-4" />
        <PageHeader
          eyebrow="Settings"
          title="Privacy and data"
          description="Legal documents, cookie choices, marketing preferences, export, and account deletion."
        />
        <div className="mt-8 space-y-3">
          {[
            ['Privacy Policy', '/privacy'],
            ['Terms of Service', '/terms'],
            ['Community Guidelines', '/community-guidelines'],
            ['Cookie Policy', '/cookies'],
            ['Copyright / DMCA', '/copyright'],
            ['Notification preferences', '/settings/notifications'],
          ].map(([label, to]) => (
            <Link
              key={to}
              to={to}
              className="block rounded-lg border border-white/[0.08] bg-[#0c0c0c] px-4 py-3 text-sm text-gray-200 hover:border-white/20"
            >
              {label}
            </Link>
          ))}
          <button
            type="button"
            className="w-full text-left rounded-lg border border-white/[0.08] bg-[#0c0c0c] px-4 py-3 text-sm text-gray-200 hover:border-white/20 min-h-[44px]"
            onClick={() => setCookieOpen(true)}
          >
            Cookie preferences
          </button>
        </div>

        {isAuthenticated ? (
          <div className="mt-8 space-y-3">
            <h2 className="font-display text-xl text-white">Your data</h2>
            <Button
              type="button"
              variant="secondary"
              className="min-h-[44px]"
              loading={exportBusy}
              onClick={() => {
                setExportBusy(true);
                setStatus('');
                void apiService
                  .exportAccountData()
                  .then((res: any) => {
                    const blob = new Blob([JSON.stringify(res.data || res, null, 2)], {
                      type: 'application/json',
                    });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'bilt-account-export.json';
                    a.click();
                    URL.revokeObjectURL(url);
                    setStatus('Export downloaded.');
                  })
                  .catch((err: any) => setStatus(err?.message || 'Export failed.'))
                  .finally(() => setExportBusy(false));
              }}
            >
              Download account data
            </Button>
            <Button
              type="button"
              variant="secondary"
              className="min-h-[44px]"
              onClick={() => {
                void apiService
                  .submitPrivacyRequest({ request_type: 'marketing_opt_out' })
                  .then(() => setStatus('Marketing email opt-out recorded.'));
              }}
            >
              Opt out of marketing email
            </Button>
            <p className="text-sm text-gray-500">
              Account deletion is available in the mobile app Settings, or contact privacy@biltspec.com. Web self-serve
              deletion can be completed through support while the web delete flow is finalized.
            </p>
          </div>
        ) : (
          <p className="mt-8 text-sm text-gray-500">
            <Link to="/login" className="text-orange-300">
              Sign in
            </Link>{' '}
            to export data or manage marketing preferences.
          </p>
        )}
        {status ? (
          <p className="mt-4 text-sm text-emerald-300" role="status">
            {status}
          </p>
        ) : null}
      </div>
      <CookiePreferencesModal isOpen={cookieOpen} onClose={() => setCookieOpen(false)} />
      <Footer />
    </PageShell>
  );
};

export default PrivacyCenterPage;
